/**
 * `InstallCoverageCard` — ADR-0211 §5. "What Sill sees on {domain}":
 * the measured install layers (people / agents without JavaScript /
 * agent card), the store when it applies, and at most ONE next step.
 * Shown on the onboarding completion screen and in Settings → Install.
 *
 * 2026-10-29 redesign (founder + UX / design / content / product
 * review): a checkmark strip leads the card, rows carry a status chip
 * and one plain line each (`coverage-view.ts` is the single reading of
 * the payload), amber only when the merchant must act, and the next
 * step is a text link, never a second primary button. Fail-soft: a read
 * failure renders nothing.
 *
 * Round 2 (founder 2026-10-29): on the completion screen the rows ARE
 * the checklist, so the strip, its divider and the Re-check icon are
 * Settings only; the card still re-reads on its own while waiting.
 */

import { ArrowRight, Check, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Site } from '@sill/types';
import type { InstallCoverage, InstallCoverageAction } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { Card } from '../../components/atoms/index.js';
import { installUpdateCopy } from '../../lib/install-update-copy.js';
import { useC } from '../../lib/theme.js';
import { coverageView, type CoverageRow, type CoverageRowState } from './coverage-view.js';
import { PlatformMark } from './PlatformMark.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export type CoverageNavigate = (target: 'integrations' | 'endpoint' | 'cloudflare' | 'install') => void;

export interface InstallCoverageCardProps {
  readonly site: Site;
  /**
   * Where a CTA lands. `cloudflare` and `integrations` both mean
   * Settings → Integrations (the Cloudflare sub-section lives there);
   * `endpoint` is Settings → Endpoint (the snippet). Omitted → CTAs
   * render as plain text with no button (the Settings placement, where
   * the merchant is already on the page).
   */
  readonly onNavigate?: CoverageNavigate | undefined;
  /** Settings placement: drop the outer Card (the Section supplies it). */
  readonly bare?: boolean | undefined;
  /**
   * `onboarding` (the completion screen): the rows alone, no strip,
   * divider, Re-check or install-version row. `settings` (default): all
   * of them.
   */
  readonly variant?: 'onboarding' | 'settings' | undefined;
  /** Every successful read, so the host can word its own summary. */
  readonly onCoverage?: ((c: InstallCoverage) => void) | undefined;
}

const STACK_LABEL: Record<string, string> = {
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  wordpress: 'WordPress',
  cloudflare: 'Custom site',
  other_known: 'Custom site',
  unknown: 'Custom site',
};
const VENDOR_LABEL: Record<string, string> = {
  webflow: 'Webflow', framer: 'Framer', ghost: 'Ghost', squarespace: 'Squarespace', wix: 'Wix',
  bigcommerce: 'BigCommerce', magento: 'Magento',
};
const CDN_LABEL: Record<string, string> = {
  cloudflare: 'Cloudflare', fastly: 'Fastly', cloudfront: 'CloudFront', akamai: 'Akamai',
};

export function stackLabel(c: Pick<InstallCoverage, 'platform' | 'vendor_hint' | 'cdn'>): string {
  const base = (c.vendor_hint !== null ? VENDOR_LABEL[c.vendor_hint] : undefined) ?? STACK_LABEL[c.platform] ?? 'Custom site';
  const cdn = CDN_LABEL[c.cdn];
  return cdn !== undefined ? `${base} · ${cdn}` : base;
}

const REDIRECT_HOST_LABEL: Record<string, string> = {
  netlify: 'Netlify', vercel: 'Vercel', nginx: 'nginx', apache: 'Apache', webflow: 'Webflow',
  squarespace: 'Squarespace', wix: 'Wix', generic: 'your host',
};

/** §5.3 — action copy. One shown at a time, as a text link under the rows. */
export function actionCopy(action: InstallCoverageAction, domain: string): { body: string; cta: string; target: 'integrations' | 'endpoint' | 'cloudflare' | 'docs_cloudflare' | 'redirect' } {
  switch (action.kind) {
    case 'deploy_worker':
      return {
        body: `${domain} runs through Cloudflare. The Sill Worker sees every agent, including those that never run JavaScript, and serves your discovery files.`,
        cta: 'Deploy the Cloudflare Worker',
        target: 'cloudflare',
      };
    case 'install_plugin':
      return {
        body: `${domain} runs on WordPress. The Sill plugin sees every request that reaches WordPress and serves your discovery files.`,
        cta: 'Download the plugin',
        target: 'integrations',
      };
    case 'add_cloudflare':
      if (action.reason === 'www_not_proxied') {
        return {
          body: `${domain} is on Cloudflare, but the record that serves your pages (usually www) has a grey cloud, so Cloudflare is bypassed. In Cloudflare, open DNS, click that record and turn Proxy status on (orange). Then deploy the Sill Worker here.`,
          cta: 'Turn the orange cloud on',
          target: 'docs_cloudflare',
        };
      }
      return {
        body: 'Most AI agents read pages without running JavaScript, so a script tag never sees them. A free Cloudflare account in front of your domain fixes that in about ten minutes, and nothing on your site changes. Then deploy the Sill Worker here with one click.',
        cta: 'Set up Cloudflare (free)',
        target: 'docs_cloudflare',
      };
    case 'paste_embed':
      return {
        body: `We could not find the snippet on your homepage. Paste it on every page of ${domain}.`,
        cta: 'Show the snippet',
        target: 'endpoint',
      };
    case 'add_redirect':
      return {
        body: `Tools like ChatGPT look for your agent card at /.well-known/agent-card.json on ${domain}. A one-line redirect to Sill keeps it current.`,
        cta: `Show the redirect for ${REDIRECT_HOST_LABEL[action.host] ?? 'your host'}`,
        target: 'redirect',
      };
    case 'connect_store':
      return {
        body: `Connect ${action.store === 'shopify' ? 'Shopify' : 'WooCommerce'} so agents can read your catalog and orders.`,
        cta: `Connect ${action.store === 'shopify' ? 'Shopify' : 'WooCommerce'}`,
        target: 'integrations',
      };
  }
}

const DOCS_CLOUDFLARE_URL = 'https://docs.sill.so/install/cloudflare';
const DOCS_REDIRECT_URL = 'https://docs.sill.so/install/agent-card-redirect';

function Glyph({ state }: { state: CoverageRowState }): JSX.Element {
  const C = useC();
  const covered = state === 'live' || state === 'installed';
  const tone =
    covered ? { bg: C.limeDim, border: C.limeFg, fg: C.limeFg }
    : state === 'action' ? { bg: C.amberDim, border: C.amberFg, fg: C.amberFg }
    : state === 'checking' ? { bg: 'transparent', border: C.skyFg, fg: C.skyFg }
    : state === 'optional' ? { bg: 'transparent', border: C.borderSoft, fg: C.dim }
    : { bg: 'transparent', border: C.border, fg: C.muted };
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center flex-shrink-0"
      style={{ width: 18, height: 18, borderRadius: 2, background: tone.bg, border: `1px solid ${tone.border}`, color: tone.fg, fontFamily: monoFont, fontSize: 11, fontWeight: 600, lineHeight: 1 }}
    >
      {covered ? <Check size={12} strokeWidth={3} />
        : state === 'action' ? '!'
        : state === 'checking' ? <span style={{ width: 6, height: 6, borderRadius: 3, background: C.skyFg }} />
        : state === 'optional' ? '–'
        : null}
    </span>
  );
}

function Chip({ state, text }: { state: CoverageRowState; text: string }): JSX.Element {
  const C = useC();
  const tone =
    state === 'live' ? { bg: C.mintDim, fg: C.mintFg }
    : state === 'installed' || state === 'checking' ? { bg: C.skyDim, fg: C.skyFg }
    : state === 'action' ? { bg: C.amberDim, fg: C.amberFg }
    : { bg: C.surface2, fg: C.muted };
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{ fontFamily: monoFont, fontSize: 9.5, letterSpacing: 0.8, fontWeight: 500, whiteSpace: 'nowrap', color: tone.fg, background: tone.bg, borderRadius: 2, padding: '2px 6px' }}
    >
      {state === 'installed' ? <span aria-hidden className="sill-pulse" style={{ width: 5, height: 5, borderRadius: 3, background: tone.fg }} /> : null}
      {text}
    </span>
  );
}

function Row({ row, action }: { row: CoverageRow; action?: { label: string; onClick: () => void } | undefined }): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-start gap-3" data-testid={`coverage-row-${row.id}`} data-state={row.state}>
      <span style={{ marginTop: 1 }}><Glyph state={row.state} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span style={{ fontFamily: sansFont, fontSize: 13, fontWeight: 500, color: row.state === 'optional' ? C.muted : C.text }}>{row.label}</span>
          <Chip state={row.state} text={row.chip} />
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3" style={{ marginTop: 2 }}>
          <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{row.description}</span>
          {row.link !== undefined ? (
            <a
              href={row.link.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1"
              style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' }}
              data-testid={`coverage-row-${row.id}-link`}
            >
              {row.link.label} <ArrowRight size={12} />
            </a>
          ) : null}
          {action !== undefined ? (
            <button
              type="button"
              onClick={action.onClick}
              className="inline-flex items-center gap-1"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, color: C.text, textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' }}
              data-testid={`coverage-row-${row.id}-action`}
            >
              {action.label} <ArrowRight size={12} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Settings -> Install carries the update steps (#install-update). Scroll
 *  there when it is on this page; otherwise open the Install tab. */
function goToUpdateSteps(onNavigate: CoverageNavigate | undefined): void {
  const el = typeof document !== 'undefined' ? document.getElementById('install-update') : null;
  if (el !== null) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  if (onNavigate !== undefined) onNavigate('install');
}

/** While the embed is installed but quiet, re-read every 15 s for up to
 *  2 minutes so the row turns live without a click. */
const POLL_MS = 15_000;
const POLL_MAX = 8;

export function InstallCoverageCard({ site, onNavigate, bare, variant = 'settings', onCoverage }: InstallCoverageCardProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const [coverage, setCoverage] = useState<InstallCoverage | null>(null);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  const alive = useRef(true);
  const polls = useRef(0);
  // Re-arm on (re)mount: React StrictMode runs mount → cleanup → mount in
  // development, and a cleanup-only effect left `alive` false forever, so
  // the card never left "Checking…" on the dev server.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    setChecking(true);
    try {
      const c = await api.sites.getInstallCoverage(site.site_id);
      if (alive.current) { setCoverage(c); setFailed(false); onCoverage?.(c); }
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setChecking(false);
    }
  }, [api, site.site_id, onCoverage]);

  useEffect(() => { void load(); }, [load]);

  const quiet = coverage !== null && coverage.browser.state !== 'live' && coverage.browser.installed === true;
  useEffect(() => {
    if (!quiet || polls.current >= POLL_MAX) return undefined;
    const t = setTimeout(() => { polls.current += 1; void load(); }, POLL_MS);
    return () => clearTimeout(t);
  }, [quiet, coverage, load]);

  if (failed) return null;

  const view = coverage !== null ? coverageView(coverage, site.domain) : null;
  const all_covered = view !== null && view.rows.every((r) => r.state === 'live' || r.state === 'installed' || r.state === 'optional');
  const settings = variant === 'settings';

  const body = (
    <div data-testid="install-coverage-card" data-variant={variant}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted }}>
            WHAT SILL SEES ON {site.domain.toUpperCase()}
          </div>
          {view !== null ? (
            <div data-testid="coverage-summary" style={{ fontFamily: sansFont, fontSize: 13, color: all_covered ? C.limeFg : C.text, marginTop: 6 }}>
              {view.summary}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {coverage !== null ? <PlatformMark coverage={coverage} fallbackLabel={stackLabel(coverage)} /> : null}
          {coverage !== null && settings ? (
            <button
              type="button"
              onClick={() => void load()}
              disabled={checking}
              aria-label="Re-check"
              className="inline-flex items-center gap-1.5"
              style={{ background: 'transparent', border: 'none', padding: 2, fontFamily: sansFont, fontSize: 12, color: C.muted, cursor: checking ? 'default' : 'pointer' }}
              data-testid="install-readiness-recheck"
            >
              <RefreshCw size={13} style={checking ? { animation: 'spin 1.4s linear infinite' } : undefined} />
              Re-check
            </button>
          ) : null}
        </div>
      </div>

      {view === null ? (
        <div className="flex items-center gap-2 mt-4" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          <RefreshCw size={14} style={{ animation: 'spin 1.4s linear infinite' }} />
          Checking what reaches Sill from your site…
        </div>
      ) : (
        <>
          {settings ? (
          <div
            data-testid="coverage-strip"
            // Phone width: the rows below are already the checkmark list
            // right under the summary line, so the strip would only repeat
            // them and push the details down.
            className={`hidden sm:grid ${view.rows.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-4'} gap-1.5 mt-4`}
          >
            {view.rows.map((r) => (
              <div
                key={r.id}
                data-testid={`coverage-strip-${r.id}`}
                data-state={r.state}
                className="flex items-center gap-2"
                style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2, padding: '7px 10px' }}
              >
                <Glyph state={r.state} />
                <span style={{ fontFamily: sansFont, fontSize: 12, fontWeight: 500, color: r.state === 'optional' || r.state === 'not_covered' ? C.muted : C.text, whiteSpace: 'nowrap' }}>{r.short}</span>
              </div>
            ))}
          </div>
          ) : null}

          {/* No rule between the strip and the rows (founder 2026-10-29,
              same as the completion step): the strip is the summary, the
              rows are its detail. */}
          <div className="flex flex-col gap-3 mt-4" data-testid="coverage-rows">
            {view.rows.map((r) => <Row key={r.id} row={r} />)}
            {/* ADR-0239 — install version. Settings only: on a fresh
                install it is noise. */}
            {variant === 'settings' && site.install_update_available === true ? (
              <Row
                row={{
                  id: 'install-update-available' as CoverageRow['id'],
                  label: 'Install version',
                  short: 'Version',
                  state: 'action',
                  chip: 'UPDATE',
                  description: site.install_update !== undefined
                    ? `${installUpdateCopy(site.install_update).versions} available`
                    : 'update available',
                }}
                action={{ label: 'Update', onClick: () => goToUpdateSteps(onNavigate) }}
              />
            ) : variant === 'settings' && site.install_update_available === false && coverage !== null && coverage.server_side.source !== null ? (
              <Row
                row={{
                  id: 'install-version' as CoverageRow['id'],
                  label: 'Install version',
                  short: 'Version',
                  state: 'live',
                  chip: 'CURRENT',
                  description: `${coverage.server_side.source === 'worker' ? 'Cloudflare Worker' : 'WordPress plugin'} · up to date`,
                }}
              />
            ) : null}
          </div>

          {coverage !== null && coverage.action !== null ? (() => {
            const a = actionCopy(coverage.action, site.domain);
            const external = a.target === 'docs_cloudflare' ? DOCS_CLOUDFLARE_URL : a.target === 'redirect' ? DOCS_REDIRECT_URL : null;
            const internal = a.target === 'cloudflare' || a.target === 'integrations' || a.target === 'endpoint' ? a.target : null;
            const linkStyle = { fontFamily: sansFont, fontSize: 13, fontWeight: 600, color: C.text, textDecoration: 'underline', textUnderlineOffset: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer' } as const;
            return (
              <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${C.borderSoft}` }} data-testid="coverage-action" data-kind={coverage.action.kind}>
                <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 6px', maxWidth: 600 }}>{a.body}</p>
                {external !== null ? (
                  <a href={external} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1" style={linkStyle} data-testid="coverage-cta">
                    {a.cta} <ArrowRight size={13} />
                  </a>
                ) : internal !== null && onNavigate !== undefined ? (
                  <button type="button" onClick={() => onNavigate(internal)} className="inline-flex items-center gap-1" style={linkStyle} data-testid="coverage-cta">
                    {a.cta} <ArrowRight size={13} />
                  </button>
                ) : null}
              </div>
            );
          })() : null}
        </>
      )}
    </div>
  );

  if (bare === true) return body;
  return (
    <Card className="p-4 sm:p-5" data-testid="install-readiness-check">
      {body}
    </Card>
  );
}
